import { useCallback, useRef } from 'react';
import { MutationKey } from '@tanstack/react-query';
import { useDebouncedSave } from '@dealstack/frontend/shared/hooks/useDebouncedSave';

interface UseDebouncedPendingSaveOptions<T, R> {
	mutationKey: MutationKey;
	debounceMs: number;
	mutationFn: (snapshot: T) => Promise<R | null>;
	onSuccess?: (response: R | null) => void;
	onError?: (error: unknown) => void;
}

interface UseDebouncedPendingSaveReturn<T> {
	save: (snapshot: T) => void;
	flush: () => Promise<boolean>;
	cancel: () => void;
}

/**
 * useDebouncedSave + a single-slot pending snapshot. Each save() replaces the
 * pending value; when the debounce fires, the latest snapshot is taken (and
 * cleared) before invoking mutationFn. If no snapshot is pending (e.g. after
 * cancel), mutationFn is skipped.
 */
export function useDebouncedPendingSave<T, R>({
	mutationKey,
	debounceMs,
	mutationFn,
	onSuccess,
	onError,
}: UseDebouncedPendingSaveOptions<T, R>): UseDebouncedPendingSaveReturn<T> {
	const pendingRef = useRef<T | null>(null);
	const mutationFnRef = useRef(mutationFn);
	mutationFnRef.current = mutationFn;

	const { schedule, flush, cancel } = useDebouncedSave({
		mutationKey,
		debounceMs,
		mutationFn: async () => {
			const snapshot = pendingRef.current;
			pendingRef.current = null;
			if (snapshot === null) {
				return null;
			}
			return mutationFnRef.current(snapshot);
		},
		onSuccess,
		onError,
	});

	const save = useCallback(
		(snapshot: T) => {
			pendingRef.current = snapshot;
			schedule();
		},
		[schedule]
	);

	const cancelAndClear = useCallback(() => {
		pendingRef.current = null;
		cancel();
	}, [cancel]);

	return { save, flush, cancel: cancelAndClear };
}
